import React from 'react';
import { ListContent } from '@components/Feed/ListViews/components/ListContent';
import { ListMinimalUser } from '@components/Feed/ListViews/components/ListMinimalUser';
import { ReleaseInfo } from '@components/Feed/ListViews/components/ReleaseInfo';
import { ReleasePill } from '@components/Feed/ListViews/components/ReleasePill';
import { ToggledAvatar } from '@components/Feed/ListViews/components/ToggledAvatar';
import { ReleasePlayButton } from '@components/basics/ReleasePlayButton';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { MinimalUserFragmentDoc, ReleasePillFragmentDoc } from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import {
  FeedListViewContainer,
  FeedListViewCoverContainer,
  FeedListViewDisplayWrapper,
  FeedListViewSubText,
  FeedListViewWrapper,
} from './components/shared';

export const ACTION_TYPE = {
  RELEASE_LIKED: 'liked',
  RLEASE_UNLIKED: 'unliked',
  RELEASE_DROPPED: 'dropped',
} as const;

export type ActionType = (typeof ACTION_TYPE)[keyof typeof ACTION_TYPE];

export const SingleReleaseList = ({
  userFrag,
  releaseFrag,
  isoDateString,
  action,
  releaseFeed = false,
  hideArtist = false,
}: {
  userFrag: FragmentType<MinimalUserFragmentDoc>;
  releaseFrag: FragmentType<ReleasePillFragmentDoc> | null;
  isoDateString: string;
  action: ActionType;
  releaseFeed?: boolean;
  hideArtist?: boolean;
}) => {
  const { tabletLandscape, tablet, laptop, desktop } = MediaQuery.useContainer();
  const forceMobileView = releaseFeed && laptop && !desktop;

  const release = getFragment(ReleasePillFragmentDoc, releaseFrag);

  const actionText = releaseFeed ? `${action} this Sound` : action;

  const {
    avatar,
    username,
    publicAddress,
    webappUri: userWebappUri,
  } = getFragment(MinimalUserFragmentDoc, userFrag);

  const title = release?.title;
  const name = release?.artist.name;
  const webappUri = release?.artist.webappUri;

  const showReleaseInfo = !!title && webappUri && !!name && !tabletLandscape && !releaseFeed;

  const user = (
    <FeedListViewWrapper margin="first">
      <ListMinimalUser userFrag={userFrag} forceMobileView={forceMobileView} />
    </FeedListViewWrapper>
  );

  return (
    <FeedListViewContainer>
      {!!release && !tabletLandscape && !releaseFeed && (
        <FeedListViewCoverContainer>
          <ReleasePlayButton playButtonSize={tablet ? 'm' : 's'} releaseFrag={release} />
        </FeedListViewCoverContainer>
      )}
      {releaseFeed && (
        <ToggledAvatar
          src={avatar?.url}
          username={username}
          walletAddress={publicAddress}
          userWebappUri={userWebappUri}
          forceMobileView={forceMobileView}
        />
      )}
      <ListContent isoDateString={isoDateString} truncateChildren forceMobileView={forceMobileView}>
        {user}
        <FeedListViewSubText>{actionText}</FeedListViewSubText>

        {!releaseFeed && !!releaseFrag && (
          <FeedListViewDisplayWrapper media="tabletLandscape">
            <FeedListViewWrapper>
              <ReleasePill release={releaseFrag} hideArtist={hideArtist} />
            </FeedListViewWrapper>
          </FeedListViewDisplayWrapper>
        )}

        {showReleaseInfo && (
          <>
            {SPACE}
            <ReleaseInfo
              releaseTitle={title}
              releaseUri={release.webappUri}
              artist={
                hideArtist
                  ? null
                  : {
                      artistName: name,
                      artistUri: webappUri,
                    }
              }
            />
          </>
        )}
      </ListContent>
    </FeedListViewContainer>
  );
};
